import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useRef,
  ChangeEvent,
  useEffect,
} from "react";
import { UploadFileProps } from "../../types/interface";
import { TestResponse } from "../../types/interface/ProjectTrain.interface";
import { toast } from "react-toastify";

// Define the shape of the context data
interface TestContextType {
  filename: UploadFileProps;
  setFilename: (file: UploadFileProps) => void;
  fileUpload: File | null;
  setFileUpload: (file: File | null) => void;
  isUploading: boolean;
  setIsUploading: (uploading: boolean) => void;
  progress: number;
  setProgress: (progress: number) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleCardClick: () => void;
  handleFileDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  handleModelChange: (event: string) => void; // Assuming this is correct
  setModel: React.Dispatch<React.SetStateAction<string | undefined>>; // Updated type
  model: string | undefined; // Updated type
  sliderValue: number | number[];
  setSliderValue: React.Dispatch<React.SetStateAction<number | number[]>>;
  compare: boolean;
  setCompare: React.Dispatch<React.SetStateAction<boolean>>;
  loadInference: boolean;
  setLoadInference: React.Dispatch<React.SetStateAction<boolean>>;
  loadOtherInference: boolean;
  setLoadOtherInference: React.Dispatch<React.SetStateAction<boolean>>;
  handleCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSliderChange: (event: Event, newValue: number | number[]) => void;
  handleModelChangeOther: (event: string) => void; // Assuming this is correct
  setModelOther: React.Dispatch<React.SetStateAction<string | undefined>>; // Updated type
  modelOther: string | undefined; // Updated type
  sliderValueOther: number | number[];
  setSliderValueOther: React.Dispatch<React.SetStateAction<number | number[]>>;
  handleSliderChangeOther: (event: Event, newValue: number | number[]) => void;
  firstResult: TestResponse | null;
  setFirstResult: React.Dispatch<React.SetStateAction<TestResponse | null>>;
  nextResult: TestResponse | null;
  setNextResult: React.Dispatch<React.SetStateAction<TestResponse | null>>;
}

// Create the context with a default value
const TestContext = createContext<TestContextType | undefined>(undefined);

// Define the type for the provider's props
interface TestProviderProps {
  children: ReactNode;
}

// Create the provider component
export const TestProvider: React.FC<TestProviderProps> = ({ children }) => {
  const [filename, setFilename] = useState<UploadFileProps>({
    name: "",
    size: "",
    format: "",
    dimensions: "",
  });
  const [firstResult, setFirstResult] = useState<TestResponse | null>(null);
  const [nextResult, setNextResult] = useState<TestResponse | null>(null);

  const [fileUpload, setFileUpload] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [model, setModel] = React.useState<string | undefined>("");
  const [sliderValue, setSliderValue] = useState<number | number[]>(50);
  const [sliderValueOther, setSliderValueOther] = useState<number | number[]>(
    50
  );
  const [modelOther, setModelOther] = React.useState<string | undefined>("");

  const [compare, setCompare] = useState<boolean>(false);
  const [loadInference, setLoadInference] = useState(false);
  const [loadOtherInference, setLoadOtherInference] = useState(false);
  const uploadFile = (file: File) => {
    const mockUpload = setInterval(() => {
      setProgress((prevProgress) => {
        if (prevProgress >= 100) {
          clearInterval(mockUpload);
          setIsUploading(false);
          return 100;
        }
        return prevProgress + 10;
      });
    }, 500);
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue(newValue); // no need to cast if you expect both number and number[]
  };

  const handleSliderChangeOther = (
    event: Event,
    newValue: number | number[]
  ) => {
    setSliderValueOther(newValue); // no need to cast if you expect both number and number[]
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCompare(event.target.checked);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target; // Reference to the input element
    const file = fileInput.files?.[0];
    if (file) {
      const acceptedTypes = ["image/png", "image/jpeg"];
      const rejectedExtensions = ["jifi", "jfif"]; // Explicitly reject jifi files
      const fileName = file.name;
      const fileExtension = fileName.split(".").pop()?.toLowerCase(); // Get the file extension

      const maxSizeInBytes = 10 * 1024 * 1024; // 10 MB
      if (file.size > maxSizeInBytes) {
        // Handle file size 10 MB
        setFilename({
              name: "",
              size: "",
              format: "",
              dimensions: "",
            });
        toast.error("File size exceeds the 10MB limit.", {
          position: "bottom-right",
        });
      } else {
        if (fileExtension && acceptedTypes.includes(file.type) && !rejectedExtensions.includes(fileExtension)) {
          setIsUploading(true);
          uploadFile(file);
          setFileUpload(file);

          // Get the name, size, format, and dimensions of the file
          const fileName = file.name;
          const fileSize = (file.size / 1024).toFixed(2) + " KB"; // Convert size to KB
          const fileFormat = file.type.split("/")[1]; // Get file format
          let fileDimensions = "N/A";

          const updateFileDetails = () => {
            setFilename({
              name: fileName,
              size: fileSize,
              format: fileFormat,
              dimensions: fileDimensions,
            });
          };

          if (file.type.startsWith("image/")) {
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = function () {
              fileDimensions = `${img.width} x ${img.height} px`;
              URL.revokeObjectURL(url); // Clean up after loading
              updateFileDetails();
            };
            img.src = url;
          } else {
            updateFileDetails();
          }
        } else {
          // Handle invalid file types
          toast.error(
            "Invalid file type. Please upload a PNG, JPG, or JPEG image.",
            {
              position: "bottom-right",
            }
          );
          //setFileUpload(null); // Cleanup uploaded file
          //fileInput.value = ""; // Clear the file input
        }
      }
    }
  };

  useEffect(() => {
    setSliderValueOther(50);
    setSliderValue(50);
    setModel("");
    setModelOther("");
    setFirstResult(null);
    setNextResult(null);
  }, [filename]);

  const handleCardClick = () => {
    fileInputRef.current?.click();
  };

  const handleModelChange = (event: string) => {
    setModel(event); // Update model based on the selected value
  };

  const handleModelChangeOther = (event: string) => {
    setModelOther(event); // Update model based on the selected value
  };

  const handleFileDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0];
    if (file) {
      const mockEvent = {
        target: { files: [file] },
      } as unknown as React.ChangeEvent<HTMLInputElement>;

      handleFileChange(mockEvent);
    }
  };

  // Context value that is passed to consumers
  const value = {
    filename,
    setFilename,
    fileUpload,
    setFileUpload,
    isUploading,
    setIsUploading,
    progress,
    setProgress,
    fileInputRef,
    handleFileChange,
    handleCardClick,
    handleFileDrop,
    handleModelChange, // Ensure the function signature matches the type
    setModel,
    model,
    sliderValue,
    setSliderValue,
    compare,
    setCompare,
    loadInference,
    setLoadInference,
    loadOtherInference,
    setLoadOtherInference,
    handleCheckboxChange,
    handleSliderChange,
    handleModelChangeOther,
    setModelOther,
    modelOther,
    sliderValueOther,
    setSliderValueOther,
    handleSliderChangeOther,
    firstResult,
    setFirstResult,
    nextResult,
    setNextResult,
  };

  return <TestContext.Provider value={value}>{children}</TestContext.Provider>;
};

// Create a custom hook to use the TestContext
export const useTest = (): TestContextType => {
  const context = useContext(TestContext);
  if (context === undefined) {
    throw new Error("useTest must be used within a TestProvider");
  }
  return context;
};
